import { Button, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

export interface BiPage {
  name: string;
  displayName: string;
}

interface PropsPagination {
  pages: BiPage[];
  activePage: string;
  onSelectPage: (pageName: string) => void;
}

// Barra de páginas do relatório. Fica dentro do container do visualizador,
// por isso continua visível em tela cheia.
export function PaginationBI({
  pages,
  activePage,
  onSelectPage,
}: PropsPagination) {
  const activeRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const activeIndex = pages.findIndex(page => page.name === activePage);

  // Rola só a barra (não a janela) até a aba ativa.
  useEffect(() => {
    const list = listRef.current;
    const active = activeRef.current;

    if (!list || !active) {
      return;
    }

    const left = active.offsetLeft;
    const right = left + active.offsetWidth;

    if (left < list.scrollLeft) {
      list.scrollLeft = left;
    } else if (right > list.scrollLeft + list.clientWidth) {
      list.scrollLeft = right - list.clientWidth;
    }
  }, [activePage]);

  if (pages.length <= 1) {
    return null;
  }

  const goTo = (index: number) => {
    const page = pages[index];

    if (page) {
      onSelectPage(page.name);
    }
  };

  return (
    <Flex
      align="center"
      gridGap="1"
      borderTopWidth="1px"
      borderColor="gray.700"
      bg="gray.800"
      px="2"
      py="2"
    >
      <Tooltip label="Página anterior" hasArrow>
        <IconButton
          aria-label="Página anterior"
          icon={<FiChevronLeft />}
          size="sm"
          variant="ghost"
          isDisabled={activeIndex <= 0}
          onClick={() => goTo(activeIndex - 1)}
        />
      </Tooltip>

      <Flex
        ref={listRef}
        position="relative"
        flex="1"
        overflowX="auto"
        gridGap="1"
        css={{ scrollbarWidth: 'thin' }}
        role="tablist"
        aria-label="Páginas do relatório"
      >
        {pages.map(page => {
          const isActive = page.name === activePage;

          return (
            <Button
              key={page.name}
              ref={isActive ? activeRef : undefined}
              role="tab"
              aria-selected={isActive}
              size="sm"
              flexShrink={0}
              fontWeight={isActive ? 'medium' : 'normal'}
              variant={isActive ? 'solid' : 'ghost'}
              colorScheme={isActive ? 'blue' : 'gray'}
              onClick={() => !isActive && onSelectPage(page.name)}
            >
              {page.displayName}
            </Button>
          );
        })}
      </Flex>

      <Tooltip label="Próxima página" hasArrow>
        <IconButton
          aria-label="Próxima página"
          icon={<FiChevronRight />}
          size="sm"
          variant="ghost"
          isDisabled={activeIndex === -1 || activeIndex >= pages.length - 1}
          onClick={() => goTo(activeIndex + 1)}
        />
      </Tooltip>
    </Flex>
  );
}
